<template>
  <div class='risk-score-component'  @click='togglePopup'>
    <div class="risk-score-component__result">
      <div class="table-label">{{ $t('risk.score.short') }}</div>
      <div class="result" :style="{'color': valueToResult.color}">
        {{valueToResult.text}}
      </div>
      <div class="result-label" :style="{'color': valueToResult.color}">
        {{ levelLabel(valueToResult.text) }}
      </div>
    </div>
    <v-divider
      inset
      vertical
    ></v-divider>
    <div class="risk-score-component__graph">
      <div class="graph-canvas" :style="{ width: `${graphWidth}px`, height: `${height}px` }">
        <svg :height="height" :width="graphWidth" :viewBox="`0 0 ${graphWidth} ${height}`">
          <defs>
            <!-- User-space coordinates so the base bar and the thicker moving bar share the same colors at every x -->
            <linearGradient
              id="BaseGradient"
              gradientUnits="userSpaceOnUse"
              :x1="offsetLeft"
              y1="0"
              :x2="graphWidth - offsetRight"
              y2="0"
            >
              <stop offset="0%" stop-color="#00D0B6"/>
              <stop offset="20%" stop-color="#EFC116"/>
              <stop offset="40%" stop-color="#FBA513"/>
              <stop offset="60%" stop-color="#FF7D00"/>
              <stop offset="80%" stop-color="#F44440"/>
              <stop offset="100%" stop-color="#E132A0"/>
            </linearGradient>
            <filter
              v-for="(item, letterIndex) in letters"
              :key="`shadow-${letterIndex}`"
              :id="shadowFilterId(letterIndex + 1)"
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
            >
              <feDropShadow dx="0" dy="0" stdDeviation="3" :flood-color="item.color" flood-opacity="0.6"/>
            </filter>
          </defs>
          <rect :x="scorePosition()" :y="Math.floor((height/2))" :width="graphWidth-offsetRight-scorePosition()" height="15" rx="8" ry="8" class="score__shadow"></rect>
          <rect :x="offsetLeft" :y="barY" :width="graphWidth-offsetLeft-offsetRight" :height="barHeight" style="fill:url(#BaseGradient)"></rect>
          <rect
            :x="scorePosition()"
            :y="scoreBarY"
            :width="scoreBarWidth()"
            :height="scoreBarHeight"
            :rx="scoreBarHeight / 2"
            :ry="scoreBarHeight / 2"
            class="score"
          >
            <animate :id='animationId' attributeName="x" attributeType="XML" begin="0s" :dur="`${animationDuration}s`" :from="fromAnimate" :to="scorePosition()"/>
            <!-- Grows together with x so the opaque bar never sticks out past the end of the line -->
            <animate attributeName="width" attributeType="XML" :begin="`${animationId}.begin`" :dur="`${animationDuration}s`" from="0" :to="scoreBarWidth()"/>
          </rect>
          <g v-for="index in letters.length" :key="`circle-${index}`">
            <circle
              :r="circleRadius"
              :cx="getCirclePointPosition(index).x"
              :cy="getCirclePointPosition(index).y"
              fill="#FFFFFF"
            />
            <g v-if="isInRange(index)">
              <!-- Keeps the active circle hidden until the animated line reaches it -->
              <animate
                v-if="fillDelay(index) > 0"
                attributeName="opacity"
                values="0"
                :begin="`${animationId}.begin`"
                :dur="`${fillDelay(index)}s`"
              />
              <circle
                :r="activeCircle.radius + activeCircle.innerRingWidth + activeCircle.outerRingWidth"
                :cx="getCirclePointPosition(index).x"
                :cy="getCirclePointPosition(index).y"
                :fill="letters[index-1].color"
                :filter="`url(#${shadowFilterId(index)})`"
              />
              <circle
                :r="activeCircle.radius + activeCircle.innerRingWidth"
                :cx="getCirclePointPosition(index).x"
                :cy="getCirclePointPosition(index).y"
                fill="#FFFFFF"
              />
              <circle
                :r="activeCircle.radius"
                :cx="getCirclePointPosition(index).x"
                :cy="getCirclePointPosition(index).y"
                :fill="letters[index-1].color"
              />
            </g>
          </g>
          <text
            v-for="index in letters.length"
            :key="`letter-${index}`"
            :x="getCirclePointPosition(index).x"
            :y="getCirclePointPosition(index).y"
            :fill="isInRange(index)? '#FFFFFF': inactiveLetterColor"
            text-anchor="middle"
            dy=".35em"
            dx=".01em"
            class="section-letter"
          >
            <animate
              v-if="isInRange(index) && fillDelay(index) > 0"
              attributeName="fill"
              :values="inactiveLetterColor"
              :begin="`${animationId}.begin`"
              :dur="`${fillDelay(index)}s`"
            />
            {{letters[index -1].letter.toUpperCase()}}
          </text>
          <text
            v-for="index in letters.length"
            :key="`label-${index}`"
            :x="getCirclePointPosition(index).x"
            :y="getCirclePointPosition(index).y + labelOffset"
            :fill="letters[index-1].color"
            text-anchor="middle"
            class="section-label"
          >
            {{ levelLabel(letters[index-1].letter) }}
          </text>
        </svg>
        <lottie-animation
          v-if="showLocationPin"
          class="location-pin"
          :style="locationPinStyle()"
          :animationData="locationAnimation"
          :loop="false"
        />
      </div>
    </div>
    <risk-score-popup
      :visible='visible'
      :value='value'
      :score='valueToResult'
      :domains='domains'
      :detailsSeries='detailsSeries'
      :labels='labels'
      :missingData='missingData'
      @closed='closeRiskScorePopup'
      :optimized='optimized'
      :recalculate-required='recalculateRequired'
      @recalculate='recalculateRiskScore'
    />
  </div>
</template>

<script>
import LottieAnimation from 'lottie-web-vue';
import RiskScorePopup from '@/components/popup/RiskScorePopup.vue';
import locationAnimationData from '@/assets/icons/animations/location.json';

export default {
  name: 'RiskScoreWidget',
  components: { LottieAnimation, RiskScorePopup },
  props: {
    missingData: {
      type: Object,
      default: () => ({
        hijack: false,
        smtp: false,
        dns: false,
        cloud: false,
        dataleaks: false,
        employees: false,
        waf: false,
        tech: false,
        ports: false,
      }),
    },
    recalculateRequired: {
      type: Boolean,
      default: false,
    },
    optimized: {
      type: Boolean,
      default: false,
    },
    graphWidth: {
      type: Number,
      default: () => 905,
    },
    height: {
      type: Number,
      default: () => 120,
    },
    value: {
      type: Number,
      default: () => 0,
    },
    details: {
      type: Object,
      default: () => {},
    },
    domains: {
      type: Array,
      required: true,
      default: () => [],
    },
    letters: {
      type: Array,
      default() {
        return [
          {
            letter: 'a',
            color: '#29CE9C',
          },
          {
            letter: 'b',
            color: '#F0BF16',
          },
          {
            letter: 'c',
            color: '#FBA212',
          },
          {
            letter: 'd',
            color: '#FF7A03',
          },
          {
            letter: 'e',
            color: '#ED3D68',
          },
          {
            letter: 'f',
            color: '#E132A0',
          },
        ];
      },
    },
  },
  data() {
    return {
      visible: false,
      showLocationPin: false,
      locationPinTimer: null,
    };
  },
  created() {
    this.activateAnimate();
  },
  beforeDestroy() {
    clearTimeout(this.locationPinTimer);
  },
  watch: {
    value() {
      this.activateAnimate();
    },
  },
  computed: {
    detailsSeries() {
      return Object.values(this.details);
    },
    labels() {
      return Object.keys(this.details);
    },
    fromAnimate() {
      return this.graphWidth - this.offsetRight;
    },
    offsetLeft: () => 40,
    offsetRight: () => 30,
    // SMIL syncbase references require '-' in ids to be escaped, so keep this id camelCase
    animationId: () => 'scoreAnimation',
    animationDuration: () => 4,
    inactiveLetterColor: () => '#A9B6C3',
    barHeight: () => 15,
    scoreBarHeight: () => 21,
    // Returned from a computed so Vue doesn't make the large animation JSON reactive
    locationAnimation: () => locationAnimationData,
    // size: rendered box in px; tipX/tipY: where the pin tip rests inside the 430x430 Lottie canvas (215, 373)
    locationPin: () => ({
      size: 56,
      tipX: 215 / 430,
      tipY: 373 / 430,
    }),
    barY() {
      return Math.floor(this.height / 2);
    },
    scoreBarY() {
      return this.barY - (this.scoreBarHeight - this.barHeight) / 2;
    },
    circleRadius: () => 15,
    // Distance from circle center to label baseline, clears the active circle and its shadow
    labelOffset: () => 40,
    activeCircle: () => ({
      radius: 17,
      innerRingWidth: 2,
      outerRingWidth: 1,
    }),
    valueToResult() {
      const result = this.letters[this.indicatorIndex].letter;
      const {color} = this.letters[this.indicatorIndex];
      return {
        text: result,
        color,
      };
    },
    // Minimum (100 - value) needed for each letter from A to E; anything below the last one is F
    levelThresholds: () => [95, 81, 71, 61, 51],
    localValue() {
      return 100 - this.value;
    },
    indicatorIndex() {
      const index = this.levelThresholds.findIndex(threshold => this.localValue >= threshold);
      return index === -1 ? this.letters.length - 1 : index;
    },
    // How far the score is inside its letter range: 0 = next to the previous letter, 1 = on its own letter
    levelProgress() {
      if (this.indicatorIndex === 0) return 1;
      const upperBound = this.levelThresholds[this.indicatorIndex - 1];
      const lowerBound = this.indicatorIndex < this.levelThresholds.length
        ? this.levelThresholds[this.indicatorIndex]
        : 0;
      const progress = (upperBound - this.localValue) / (upperBound - lowerBound);
      return Math.min(1, Math.max(0, progress));
    },
  },
  methods: {
    async recalculateRiskScore(domains) {
      await this.$emit('recalculate', domains);
    },
    closeRiskScorePopup() {
      this.visible = false;
    },
    togglePopup() {
      this.visible = !this.visible;
    },
    activateAnimate() {
      const element = document.getElementById(this.animationId);
      if (element) {
        element.beginElement();
      }
      this.scheduleLocationPin();
    },
    // Remounting the Lottie component replays it once, after the slider has stopped
    scheduleLocationPin() {
      clearTimeout(this.locationPinTimer);
      this.showLocationPin = false;
      this.locationPinTimer = setTimeout(() => {
        this.showLocationPin = true;
      }, this.animationDuration * 1000);
    },
    // Places the pin tip on top of the slider's rounded end
    locationPinStyle() {
      const { size, tipX, tipY } = this.locationPin;
      const capCenterX = this.scorePosition() + Math.min(this.scoreBarHeight, this.scoreBarWidth()) / 2;
      return {
        width: `${size}px`,
        height: `${size}px`,
        // Whole pixels so the pin isn't resampled at a sub-pixel offset
        left: `${Math.round(capCenterX - size * tipX)}px`,
        top: `${Math.round(this.scoreBarY - size * tipY)}px`,
      };
    },
    getCirclePointPosition(index) {
      const sections = (this.letters.length - 1);
      const width = this.graphWidth - this.offsetLeft - this.offsetRight;
      return {
        x: this.offsetLeft + (index - 1) * Math.floor(width / sections),
        y: Math.floor((this.height / 2) + 7.5),
      };
    },
    // The line moves linearly from right to left, so the delay is proportional to the distance travelled
    fillDelay(index) {
      const totalDistance = this.fromAnimate - this.scorePosition();
      if (totalDistance <= 0) return 0;
      const distanceToCircle = this.fromAnimate - this.getCirclePointPosition(index).x;
      return Math.max(0, (this.animationDuration * distanceToCircle) / totalDistance);
    },
    levelLabel(letter) {
      return this.$t(`risk.score.level.${letter}`);
    },
    shadowFilterId(index) {
      return `risk-score-circle-shadow-${index}`;
    },
    isInRange(index) {
      if (index === this.letters.length) return true;
      return this.getCirclePointPosition(index).x >= this.scorePosition();
    },
    scoreBarWidth() {
      return this.fromAnimate - this.scorePosition();
    },
    // Interpolates between the previous circle and the current letter's circle so the active circles always match the letter
    scorePosition() {
      const currentCircleX = this.getCirclePointPosition(this.indicatorIndex + 1).x;
      if (this.indicatorIndex === 0) return currentCircleX;
      const previousCircleX = this.getCirclePointPosition(this.indicatorIndex).x;
      return previousCircleX + (currentCircleX - previousCircleX) * this.levelProgress;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.risk-score-component {
  &:hover {
    cursor: pointer;
  }

  display: flex;
  flex-direction: row;
  box-sizing: border-box;
  border: 1px solid var(--border-primary);
  background-color: var(--bg-widget);
  border-radius: $radius-lg;
  width: 100%;
  height: 100%;

  &__result {
    min-height: inherit;
    width: rem(140);
    min-width: rem(140);
    max-width: rem(140);
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;

    .table-label {
      text-transform: uppercase;
      font-size: $font-size-sm;
      font-family: $font-family-regular;
      color: var(--text-secondary);
    }

    .result {
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: $font-family-black;
      font-size: rem(54);
      line-height: 1;
      text-transform: uppercase;
      margin: rem(4) 0;
    }

    .result-label {
      font-family: $font-family-semibold;
      font-size: $font-size-sm;
    }
  }

  &__graph {
    display: flex;
    justify-content: center;
    flex-basis: 85%;

    .graph-canvas {
      position: relative;
    }

    .location-pin {
      position: absolute;
      pointer-events: none;

      // Lottie writes its colors as fill/stroke attributes, which CSS overrides, so the pin follows the theme
      ::v-deep [fill^="rgb("] {
        fill: var(--brand-primary);
      }

      ::v-deep [stroke^="rgb("] {
        stroke: var(--brand-primary);
      }
    }

    .section-letter {
      font-family: $font-family-bold;
      font-size: $font-size-md;
      text-transform: uppercase;
    }

    .section-label {
      font-family: $font-family-semibold;
      font-size: $font-size-sm;
    }

    .score {
      fill: url(#BaseGradient);
      // Always white, like the circle rings, rather than the theme background (dark in dark mode)
      stroke: #FFFFFF;
      stroke-width: 1px;

      &__shadow {
      }
    }
  }
}
</style>
